/**
 * Search state lives entirely in the URL query string (see `useSearchFilters`), and every link into a
 * dataset page drops it. So when a user opens a dataset from the search results we record which
 * dataset they opened and the search they opened it from, letting the dataset breadcrumb take them
 * back to those exact results.
 *
 * The recorded `spold_id` is what makes this safe: a dataset reached any other way — a landing page
 * popular or recently viewed card, a shared URL, a related dataset — won't match, so it can never
 * inherit an unrelated query. Those fall back to a search for the dataset's own name.
 */

import { localStorageKeys } from 'storageKeys';

type SearchOrigin = {
  ver: string;
  system_model: string;
  // The raw query string of the search page, with or without its leading '?'
  search: string;
  // The dataset the user opened from those results
  spold_id: string;
};

const isSearchOrigin = (value: unknown): value is SearchOrigin => {
  const candidate = value as Partial<SearchOrigin> | null;
  return (
    typeof candidate?.search === 'string' &&
    typeof candidate?.ver === 'string' &&
    typeof candidate?.system_model === 'string' &&
    typeof candidate?.spold_id === 'string'
  );
};

export const saveSearchOrigin = ({
  ver,
  system_model,
  search,
  spold_id,
}: Omit<SearchOrigin, 'spold_id'> & { spold_id: string | number }) => {
  try {
    localStorage.setItem(
      localStorageKeys.SEARCH_ORIGIN,
      JSON.stringify({ ver, system_model, search, spold_id: String(spold_id) })
    );
  } catch {
    // Storage can throw when it's full or blocked; losing the search is not worth breaking the page over
  }
};

const loadSearchOrigin = (): SearchOrigin | null => {
  try {
    const stored = localStorage.getItem(localStorageKeys.SEARCH_ORIGIN);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return isSearchOrigin(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

type SearchPathParams = {
  ver: string;
  system_model: string;
  spold_id?: string;
  datasetName?: string;
};

/**
 * Where the dataset breadcrumb should point.
 *
 * Replays the recorded search when this is the dataset the user opened from it, on the same release —
 * filter values (geographies especially) aren't portable between releases, so a search from another
 * version or system model is dropped rather than restored onto the wrong one. Anything else gets a
 * search for the dataset's own name, which at least lands the user among related datasets.
 */
export const getSearchPath = ({ ver, system_model, spold_id, datasetName }: SearchPathParams) => {
  const basePath = `/${ver}/${system_model}/search`;
  const origin = loadSearchOrigin();

  const cameFromSearch =
    !!origin && !!spold_id && origin.spold_id === spold_id && origin.ver === ver && origin.system_model === system_model;

  if (cameFromSearch) {
    const recordedSearch = origin.search.replace(/^\?/, '');
    if (recordedSearch) return `${basePath}?${recordedSearch}`;
  }

  if (!datasetName) return basePath;

  return `${basePath}?${new URLSearchParams({ query: datasetName })}`;
};
